/* eslint-disable */
import { RootBanner } from "@/components/atoms/PromoBanners/RootBanner";
import { PageHeader } from "@/components/molecules/PageHeader/PageHeader";
import { SbcSetsSection } from "@/components/organisms/SBCs/SbcSetsSection/SbcSetsSection";
import { generateNavigationUrl } from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import { Fragment, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import SBCSet from "../../components/organisms/SBCs/SBCSet/SBCSet";
import { fireGTMEvent } from "../../functions/fireGTMEvent";
import { addAlert, showModal } from "../../redux/message/messageSlice";
import {
  SBCCategory,
  SBCSetSortOption,
  SBC_SET_CATEGORIES,
  SbcSet,
  downvote,
  getFilteredSbcSetsSelector,
  getSbcSetsSelector,
  hideSbcSet,
  markSbcSetAsFavorite,
  markSbcSetSolved,
  updateCategory,
  updateSearchTerm,
  updateSort,
  upvote,
} from "../../redux/sbcs/sbcSetsSlice";
import { AppDispatch } from "../../redux/store";
import { getUserSelector } from "../../redux/user/userSlice";
import "../Subscription/Subscription.css";
import { SBC_SORT_OPTIONS, SbcSetsPageToolbar } from "./SbcSetsPageToolbar";

const SbcSetsPage = () => {
  const sbcSetsReducer = useSelector(getSbcSetsSelector);
  const { currentCategory, searchTerm, currentSort } = sbcSetsReducer;
  const { user } = useSelector(getUserSelector);
  const { notCompletedSbcs, completedSbcs, hiddenSbcs } = useSelector(
    getFilteredSbcSetsSelector,
  );
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNotCompletedSBCs, setShowNotCompletedSBCs] = useState(true);
  const [showCompletedSBCs, setShowCompletedSBCs] = useState(false);
  const [showHiddenSbcs, setShowHiddenSbcs] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  useEffect(() => {
    if (searchParams.get("sort")) {
      const sort = SBC_SORT_OPTIONS.find(
        (option) => option.value === searchParams.get("sort"),
      ) as SBCSetSortOption;
      if (sort) {
        dispatch(updateSort(sort));
      }
    }
    if (searchParams.get("category")) {
      const category = SBC_SET_CATEGORIES.find(
        (option) =>
          option.label?.toLowerCase() ===
          searchParams.get("category")?.toLowerCase(),
      ) as SBCCategory;
      if (category) {
        dispatch(updateCategory(category));
      }
    }
  }, []);

  const onMarkSBCSetAsFavorite = (
    setId: number,
    setName: string,
    markFavorite: boolean,
  ) => {
    if (user.uuid) {
      dispatch(
        markSbcSetAsFavorite({
          setId,
          isFavorite: markFavorite,
        }),
      );
      dispatch(
        addAlert({
          description: markFavorite
            ? `Marked ${setName} as favorite`
            : `Removed ${setName} as favorite`,
          type: "success",
        }),
      );
      fireGTMEvent("clicked_favorite");
    } else
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "favorite-sbc",
        }),
      );
  };

  const onHideSBCSet = (setId: number, setName: string, hide: boolean) => {
    if (user.uuid) {
      dispatch(
        hideSbcSet({
          setId,
          isHidden: hide,
        }),
      );
      dispatch(
        addAlert({
          description: hide
            ? `Marked ${setName} as hidden`
            : `Removed ${setName} as hidden`,
          type: "success",
        }),
      );
      fireGTMEvent("clicked_hide");
    } else
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "hide-sbc",
        }),
      );
  };

  const onCompleteSBCSet = (
    setId: number,
    setName: string,
    didComplete: boolean,
  ) => {
    if (user.uuid) {
      dispatch(markSbcSetSolved({ setId, didComplete }));
      const description = didComplete
        ? `Completed ${setName}`
        : `Removed ${setName} as completed`;
      dispatch(
        addAlert({
          description,
          type: "success",
        }),
      );
      fireGTMEvent("clicked_completed");
    } else
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "complete-sbc",
        }),
      );
  };

  const handleCategoryChanged = (category: SBCCategory) => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      category: category.label?.toLowerCase(),
    });
    dispatch(updateCategory(category));
  };

  const handleSortByChanged = (option: SBCSetSortOption) => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      sort: option.value,
    });
    dispatch(updateSort(option));
  };

  const handleSearch = (searchTerm: string) => {
    dispatch(updateSearchTerm(searchTerm));
  };

  const onVote = (setId: number, vote: "UPVOTE" | "DOWNVOTE") => {
    if (user.uuid) {
      dispatch(vote === "UPVOTE" ? upvote({ setId }) : downvote({ setId }));
      const sbcSet = sbcSetsReducer.sbcsSets.data.find(
        (set) => set.id === setId,
      );
      fireGTMEvent("clicked_vote");
      if (vote === "UPVOTE" && !sbcSet?.wasUpvoted)
        dispatch(
          addAlert({
            description: `Upvoted ${sbcSet.name}`,
            type: "success",
          }),
        );
      else if (vote === "DOWNVOTE" && !sbcSet?.wasDownvoted)
        dispatch(
          addAlert({
            description: `Downvoted ${sbcSet.name}`,
            type: "success",
          }),
        );
    } else
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "upvote/downvote-sbc",
        }),
      );
  };

  const getSbcCard = (sbcSet: SbcSet) => (
    <NavLink
      to={`/sbc-solution/${sbcSet.name
        .replaceAll(/ /g, "-")
        .replaceAll("/", ",")
        .toLowerCase()}/${String(sbcSet.id)}`}
      className="max-mobile:w-[92vw] w-[464px] max-w-[464px] h-full"
    >
      <SBCSet
        {...sbcSet}
        solvedCount={
          sbcSet.repeatabilityMode !== "UNLIMITED" ? sbcSet.solvedCount : 0
        }
        price={user.platform === "PC" ? sbcSet.pcPrice : sbcSet.psPrice}
        platform={user.platform || "Playstation"}
        isCompleted={
          sbcSet.solvedCount === sbcSet.sbcsCount &&
          sbcSet.repeatabilityMode !== "UNLIMITED"
        }
        onCompleteSBCSet={() => {
          onCompleteSBCSet(sbcSet.id, sbcSet.name, !sbcSet.solved);
        }}
        onHideSBCSet={() => {
          onHideSBCSet(sbcSet.id, sbcSet.name, !sbcSet.isHidden);
        }}
        onMarkSBCSetAsFavorite={() => {
          onMarkSBCSetAsFavorite(sbcSet.id, sbcSet.name, !sbcSet.isFavorite);
        }}
        onUpvote={() => onVote(sbcSet.id, "UPVOTE")}
        onDownvote={() => onVote(sbcSet.id, "DOWNVOTE")}
        onClickPlayerReward={(resourceId: number) => {
          const navLink = generateNavigationUrl({
            playerRoleId: sbcSet.reward?.metaRating?.playerRoleId,
            resourceId,
            name: sbcSet.name,
          });
          navigate(navLink, {
            state: {
              from: "/",
              scrollPosition: window.scrollY,
            },
          });
        }}
      />
    </NavLink>
  );

  return (
    <main className="z-10 text-center text-white bg-new-gray-850 flex-1 h-full min-h-screen">
      <Helmet>
        <title>
          EA FC 25 SBC Solutions, AI Squad Builder, Meta Ratings and Players
          Database - EasySBC
        </title>
        <meta
          name="description"
          content="Explore Meta Ratings for EA Sports FC 25 players, get AI solutions for SBCs, see best Evolutions Players, and build meta squads in our AI Squad Builder"
        />
        <link rel="canonical" href="https://www.easysbc.io" />
      </Helmet>
      <PageHeader
        title={
          "EA FC 25 SBC Solutions, AI Squad Builder, Meta Ratings and Players Database"
        }
        isMobile={isMobile}
        maxWidth="max-w-[1600px]"
        helmetTitle="EA FC 25 SBC Solutions, AI Squad Builder, Meta Ratings and Players Database"
        helmetContent="Explore Meta Ratings for EA Sports FC 25 players, get AI solutions for SBCs, see best Evolutions Players, and build meta squads in our AI Squad Builder"
        helmetLink="https://www.easysbc.io"
        subtleTitle={true}
      />
      <RootBanner />
      <SbcSetsPageToolbar
        currentSortBy={currentSort}
        handleSortByChanged={handleSortByChanged}
        handleSearch={handleSearch}
        searchTerm={searchTerm}
        currentCategory={currentCategory}
        handleCategoryChanged={handleCategoryChanged}
      />
      <section className="mx-auto max-w-screen touch-device:max-w-content flex flex-col gap-y-4 h-full mt-4 w-full px-4 large-desktop:px-8 pb-12">
        <SbcSetsSection
          onToggleOpen={() => setShowNotCompletedSBCs(!showNotCompletedSBCs)}
          open={showNotCompletedSBCs}
          sectionName={`SBCs (${notCompletedSbcs.length})`}
        >
          {notCompletedSbcs.map((sbcSet) => (
            <Fragment key={sbcSet.id}>{getSbcCard(sbcSet)}</Fragment>
          ))}
        </SbcSetsSection>
        <SbcSetsSection
          onToggleOpen={() => setShowCompletedSBCs(!showCompletedSBCs)}
          open={showCompletedSBCs}
          sectionName={`Completed SBCs (${completedSbcs.length})`}
        >
          {completedSbcs.map((sbcSet) => (
            <Fragment key={sbcSet.id}>{getSbcCard(sbcSet)}</Fragment>
          ))}
        </SbcSetsSection>
        <SbcSetsSection
          onToggleOpen={() => setShowHiddenSbcs(!showHiddenSbcs)}
          open={showHiddenSbcs}
          sectionName={`Hidden SBCs (${hiddenSbcs.length})`}
        >
          {hiddenSbcs.map((sbcSet) => (
            <Fragment key={sbcSet.id}>{getSbcCard(sbcSet)}</Fragment>
          ))}
        </SbcSetsSection>
      </section>
    </main>
  );
};

export default SbcSetsPage;
